import React, { Fragment, useContext, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { deepCopy } from "../Shared/Utility/DeepCopy";
import InputMask from 'react-input-mask';
import CCActivity from "../Physician/Models/CCActivity";
import { abpnClient } from "../../Services/AbpnClient";
import { AuthContext } from "../Auth/AuthProvider";
import { getDateWithOffset } from "../Shared/Utility/GetDateWithOffset";

interface IProps {
    selfReportedActivity: CCActivity;
    handleRemove: (selfReportedActivity: CCActivity) => void;
    handleUpdate: (ccActivityId: number, selfReportedActivity: CCActivity) => void;
}

export function SelfReportedActivityRow(props: IProps) {
    const [originalSelfReportedActivity, setOriginalSelfReportedActivity] = useState<CCActivity>(props.selfReportedActivity);
    const [updatedSelfReportedActivity, setUpdatedSelfReportedActivity] = useState<CCActivity>(props.selfReportedActivity);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const [organizationErrorMessage, setOrganizationErrorMessage] = useState<string>("");
    const [dateErrorMessage, setDateErrorMessage] = useState<string>("");
    const [cmeErrorMessage, setCmeErrorMessage] = useState<string>("");
    const [saCmeErrorMessage, setSaCmeErrorMessage] = useState<string>("");

    const authContext = useContext(AuthContext);

    const beginEdit = () => {
        var updated = deepCopy(updatedSelfReportedActivity);
        updated.isEditing = true;
        setUpdatedSelfReportedActivity(updated);
    }

    const cancel = () => {
        if (originalSelfReportedActivity.isNew) {
            props.handleRemove(originalSelfReportedActivity);
        }
        else {
            setUpdatedSelfReportedActivity(originalSelfReportedActivity);
        }
    }
    
    const save = () => {
        clearValidation();

        if (validate()) {
            const url = "/api/Physician/SaveCCSelfReportedActivity/";
            abpnClient.post<CCActivity>(url, updatedSelfReportedActivity, authContext).then(result => {
                setOriginalSelfReportedActivity(result);
                setUpdatedSelfReportedActivity(result);
                props.handleUpdate(originalSelfReportedActivity.ccActivityId, result);
            });
        }
    }

    const clearValidation = () => {
        setOrganizationErrorMessage("");
        setDateErrorMessage("");
        setCmeErrorMessage("");
        setSaCmeErrorMessage("");
    }
    const validate = () => {
        var isValid = true;

        if (!updatedSelfReportedActivity.organizationName) {
            setOrganizationErrorMessage("Organization Name is required.");
            isValid = false;
        }

        if (!Date.parse(updatedSelfReportedActivity.completedDate)) {
            setDateErrorMessage(" Completed date invalid. Expected format: MM/DD/YYYY");
            isValid = false
        }
        var isValidCme = updatedSelfReportedActivity.cmeCredits >= 0;
        if (!isValidCme) {
            setCmeErrorMessage("CME credit amount must be 0 or greater.");
            isValid = false
        }

        var isValidSa = updatedSelfReportedActivity.saCredits >= 0;
        if (!isValidSa) {
            setSaCmeErrorMessage("SA CME credit amount must be 0 or greater.");
            isValid = false
        }

        return isValid;
    }

    const updateCompletedDate = (value: string) => {
        var updated = deepCopy(updatedSelfReportedActivity);

        if (Date.parse(value)) {
            updated.completedDate = getDateWithOffset(value).toJSON();
        }
        setUpdatedSelfReportedActivity(updated);
    }

    const updateActivityName = (value: string) => {
        setUpdatedSelfReportedActivity({ ...updatedSelfReportedActivity, activityName: value });
    }

    const updateOrganizationName = (value: string) => {
        setUpdatedSelfReportedActivity({ ...updatedSelfReportedActivity, organizationName: value });
    }

    const updateCMECredits = (value: number) => {
        setUpdatedSelfReportedActivity({ ...updatedSelfReportedActivity, cmeCredits: value });
    }

    const updateSACredits = (value: number) => {
        setUpdatedSelfReportedActivity({ ...updatedSelfReportedActivity, saCredits: value });
    }

    const updateIsPIPClinical = () => {
        setUpdatedSelfReportedActivity({ ...updatedSelfReportedActivity, isPipClinical: !updatedSelfReportedActivity.isPipClinical });
    }

    const updateIsPIPFeedback = () => {
        setUpdatedSelfReportedActivity({ ...updatedSelfReportedActivity, isPipFeedback: !updatedSelfReportedActivity.isPipFeedback });
    }

    const updateIsPatientSafety = () => {
        setUpdatedSelfReportedActivity({ ...updatedSelfReportedActivity, isPatientSafety: !updatedSelfReportedActivity.isPatientSafety });
    }

    const formatDefaultValue = (value: string) => {
        var date = getDateWithOffset(value);
        if (date == null) return ""
        var year = date.getFullYear();

        var month = (1 + date.getMonth()).toString();
        month = month.length > 1 ? month : '0' + month;

        var day = date.getDate().toString();
        day = day.length > 1 ? day : '0' + day;

        return month + '/' + day + '/' + year;
    }

    if (isConfirmingDelete) {
        return (
            <tr className="bg-pp-danger">
                <td colSpan={9}>
                    <span className="font-weight-bolder pp-font-size-smaller mx-2">
                        Do you want to delete Activity "{updatedSelfReportedActivity.activityName}"?
                        <Button variant="light" className="ml-2" onClick={() => props.handleRemove(updatedSelfReportedActivity)}>Yes, Delete</Button>
                        <Button variant="light" className="ml-2" onClick={() => setIsConfirmingDelete(false)}>No, Cancel</Button>
                    </span>
                </td>
            </tr>
        )
    }
    return (
        <tr>
            {updatedSelfReportedActivity.isEditing ?
                <Fragment>
                    <td>
                        <Form.Control
                            className="pp-required-input"
                            type="text"
                            defaultValue={updatedSelfReportedActivity.activityName}
                            onChange={(e) => updateActivityName(e.target.value)} />
                    </td>
                    <td>
                        <Form.Group controlId="organizationName">
                            <Form.Control
                                className="pp-required-input"
                                required
                                type="text"
                                defaultValue={updatedSelfReportedActivity.organizationName}
                                onChange={(e) => updateOrganizationName(e.target.value)} />
                            <span className="text-danger pp-font-size-smallest">{organizationErrorMessage}</span>
                        </Form.Group>
                    </td>
                    <td style={{ minWidth: "7rem" }}>
                        <InputMask mask="99/99/9999"
                            defaultValue={updatedSelfReportedActivity.completedDate ? formatDefaultValue(updatedSelfReportedActivity.completedDate) : null}
                            onChange={(e: any) => updateCompletedDate(e.target.value)}>
                            {(inputProps: any) => {
                                return <Form.Control {...inputProps} type="input" className="pp-required-input" />
                            }}
                        </InputMask>
                        <span className="text-danger pp-font-size-smallest">{dateErrorMessage}</span>
                    </td>
                    <td>
                        <Form.Control
                            className="pp-required-input"
                            type="number"
                            min={0}
                            value={updatedSelfReportedActivity.cmeCredits}
                            onChange={(e) => updateCMECredits(parseFloat(e.target.value))} />
                        <span className="text-danger pp-font-size-smallest">{cmeErrorMessage}</span>
                    </td>
                    <td>
                        <Form.Control
                            className="pp-required-input"
                            type="number"
                            min={0}
                            value={updatedSelfReportedActivity.saCredits}
                            onChange={(e) => updateSACredits(parseFloat(e.target.value))} />
                        <span className="text-danger pp-font-size-smallest">{saCmeErrorMessage}</span>
                    </td>
                    <td>
                        <Form.Check type="checkbox" className="mt-2 ml-1">
                            <Form.Check.Input
                                type="checkbox"
                                checked={updatedSelfReportedActivity.isPipClinical}
                                onChange={() => updateIsPIPClinical()} />
                        </Form.Check>
                    </td>
                    <td>
                        <Form.Check type="checkbox" className="mt-2 ml-1">
                            <Form.Check.Input
                                type="checkbox"
                                checked={updatedSelfReportedActivity.isPipFeedback}
                                onChange={() => updateIsPIPFeedback()} />
                        </Form.Check>
                    </td>
                    <td>
                        <Form.Check type="checkbox" className="mt-2 ml-1">
                            <Form.Check.Input
                                type="checkbox"
                                checked={updatedSelfReportedActivity.isPatientSafety}
                                onChange={() => updateIsPatientSafety()} />
                        </Form.Check>
                    </td>
                </Fragment>
                :
                <Fragment>
                    <td>
                        {updatedSelfReportedActivity.activityName}
                    </td>
                    <td>
                        {updatedSelfReportedActivity.organizationName}
                    </td>
                    <td>
                        {getDateWithOffset(updatedSelfReportedActivity.completedDate).toLocaleDateString()}
                    </td>
                    <td>
                        {updatedSelfReportedActivity.cmeCredits}
                    </td>
                    <td>
                        {updatedSelfReportedActivity.saCredits}
                    </td>
                    <td>
                        {updatedSelfReportedActivity.isPipClinical ? <span><i className="font-weight-bolder las la-check pp-icon-secondary"></i></span> : "-"}
                    </td>
                    <td>
                        {updatedSelfReportedActivity.isPipFeedback ? <i className="font-weight-bolder las la-check pp-icon-secondary"></i> : "-"}
                    </td>
                    <td>
                        {updatedSelfReportedActivity.isPatientSafety ? <i className="font-weight-bolder las la-check pp-icon-secondary"></i> : "-"}
                    </td>
                </Fragment>
            }
            <td>
                <div className="d-flex">
                    {updatedSelfReportedActivity.isEditing ?
                        <Fragment>
                            <Button variant="pp-primary" className="ml-1" onClick={() => save()}>Save</Button>
                            <Button variant="outline-dark" className="ml-1" onClick={() => cancel()}>Cancel</Button>
                        </Fragment>
                        :
                        <Fragment>
                            <Button variant="outline-pp-primary" className="ml-1" onClick={() => beginEdit()}>Edit</Button>
                            <Button variant="outline-dark" className="ml-1" onClick={() => setIsConfirmingDelete(true)}>Delete</Button>
                        </Fragment>
                    }
                </div>
            </td>
        </tr>
    );
}

export default SelfReportedActivityRow;